class BundleProductsMam extends HTMLElement {
  constructor() {
    super();

    // Constants
    this.classes = {
      active: 'is-active',
      loading: 'loading',
      checked: 'is-checked',
    };

    this.STORAGE_KEY = 'bundle_selected_ids';

    // Elements
    this.miniCart = document.querySelector('mini-cart');

    this.addButton = this.querySelector('.addto_cart');
    this.checkoutButton = this.querySelector('.checkout_button');

    this.blocks = this.querySelectorAll('.single_product');
    this.checkboxes = this.querySelectorAll('input[type="checkbox"][name="bundle_id"]');
    this.headerTabs = this.querySelectorAll('.header_tab > button.uk-button');

    this.stickyCartHeading = document.querySelector('.sticky-cart__content-heading > p');
    this.stickyCartPrice = document.querySelector('.sticky-cart__content-heading .price');

    // Bind methods
    this.onBlockClick = this.onBlockClick.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);
    this.onAddToCart = this.onAddToCart.bind(this);
    this.onCheckout = this.onCheckout.bind(this);
    this.onTabClick = this.onTabClick.bind(this);
  }

  connectedCallback() {
    this.initEvents();
    this.syncInitialState();
  }

  disconnectedCallback() {
    this.blocks.forEach(el => el.removeEventListener('click', this.onBlockClick));
    this.checkboxes.forEach(el => el.removeEventListener('change', this.onCheckboxChange));
    this.headerTabs.forEach(el => el.removeEventListener('click', this.onTabClick));

    this.addButton?.removeEventListener('click', this.onAddToCart);
    this.checkoutButton?.removeEventListener('click', this.onCheckout);
  }

  /* -----------------------------
   * Events
   * ----------------------------- */

  initEvents() {
    this.blocks.forEach(el => el.addEventListener('click', this.onBlockClick));
    this.checkboxes.forEach(el => el.addEventListener('change', this.onCheckboxChange));
    this.headerTabs.forEach(el => el.addEventListener('click', this.onTabClick));

    this.addButton?.addEventListener('click', this.onAddToCart);
    this.checkoutButton?.addEventListener('click', this.onCheckout);
  }

  onTabClick() {
    const t = new Date("2026-09-10T00:00:00Z").getTime();
    if (Date.now() >= t) return;
    
    setTimeout(() => {
      const digital = this.querySelector('.digital_edition');
      const physical = this.querySelector('.physical_edition');

      const isDigitalActive = digital?.classList.contains('uk-active');
      const isPhysicalActive = physical?.classList.contains('uk-active');

      if (isDigitalActive) {
        this.resetGroup(physical);
        this.selectFirst(digital);
      }

      if (isPhysicalActive) {
        this.resetGroup(digital);
        this.selectFirst(physical);
      }

      this.saveToLocalStorage();
      this.updateStickyCart();
    }, 500);
  }

  onBlockClick(e) {
    if (e.target.closest('input, label, button, a')) return;

    const checkbox = e.currentTarget.querySelector('input[name="bundle_id"]');
    if (!checkbox) return;

    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onCheckboxChange(e) {
    const t = new Date("2026-09-10T00:00:00Z").getTime();
    if (Date.now() >= t) return;
    const checkbox = e.target;
    const block = checkbox.closest('.single_product');
    if (!block) return;

    const digitalWrapper = block.closest('.digital_edition');

    // Only one selection allowed in digital
    if (digitalWrapper && checkbox.checked) {
      digitalWrapper
        .querySelectorAll('input[name="bundle_id"]')
        .forEach(cb => {
          if (cb !== checkbox) {
            cb.checked = false;
            cb.closest('.single_product')?.classList.remove(this.classes.checked);
          }
        });
    }

    block.classList.toggle(this.classes.checked, checkbox.checked);

    this.saveToLocalStorage();
    this.updateStickyCart();
  }

  /* -----------------------------
   * Helpers
   * ----------------------------- */
  resetGroup(wrapper) {
    wrapper
      ?.querySelectorAll('input[name="bundle_id"]')
      .forEach(cb => {
        cb.checked = false;
        cb.closest('.single_product')?.classList.remove(this.classes.checked);
      });
  }

  selectFirst(wrapper) {
    const first = wrapper?.querySelector('input[name="bundle_id"]');
    if (!first) return;

    first.checked = true;
    first.closest('.single_product')?.classList.add(this.classes.checked);
    first.dispatchEvent(new Event('change', { bubbles: true }));
  }

  getItems() {
    return [...this.checkboxes]
      .filter(cb => cb.checked)
      .map(cb => ({
        id: cb.value,
        quantity: 1,
      }));
  }

  /* -----------------------------
   * Local Storage
   * ----------------------------- */
  saveToLocalStorage() {
    const ids = [...this.checkboxes]
      .filter(cb => cb.checked)
      .map(cb => cb.value);

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(ids));

    // Notify sticky cart to sync
    document.dispatchEvent(new CustomEvent('bundle:selectionChanged'));
  }


  getFromLocalStorage() {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  /* -----------------------------
   * Sticky Cart
   * ----------------------------- */
  async updateStickyCart() {
    const ids = this.getFromLocalStorage();

    if (!ids.length) {
      this.setStickyContent('', '');
      return;
    }

    const variants = await this.fetchVariantData(ids);
    if (!variants.length) return;

    const titles = variants.map(v => v.title);
    const total = variants.reduce((sum, v) => sum + v.price, 0);

    this.setStickyContent(
      titles.join(' + '),
      this.formatMoney(total)
    );
  }

  setStickyContent(title, price) {
    if (this.stickyCartHeading) this.stickyCartHeading.textContent = title;
    if (this.stickyCartPrice) this.stickyCartPrice.textContent = price;
  }

  async fetchVariantData(ids) {
    if (!ids.length) return [];

    const results = await Promise.all(
      ids.map(id =>
        fetch(`/variants/${id}.js`)
          .then(r => r.json())
          .catch(() => null)
      )
    );

    return results.filter(Boolean);
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || '${{amount}}'
      );
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  /* -----------------------------
   * Actions
   * ----------------------------- */

  async onAddToCart(e) {
    e.preventDefault();

    const items = this.getItems();
    if (!items.length) return alert('Please select at least one product');

    this.setLoading(true);

    const sections =
      this.miniCart?.getSectionsToRender().map(s => s.id) || [];

    try {
      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      }).then(r => r.json());

      if (res.status) return this.showError(res.description);

      this.miniCart?.renderContents(res);

      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.classList.add('active');
        drawer.setAttribute('open', '');
      }

      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: { product: res },
        })
      );

      this.resetSelection();
    } catch (err) {
      console.error('Cart error:', err);
    } finally {
      this.setLoading(false);
    }
  }

  onCheckout(e) {
    e.preventDefault();

    const items = this.getItems();
    if (!items.length) return alert('Please select at least one product');

    const cartString = items.map(i => `${i.id}:${i.quantity}`).join(',');

    this.resetSelection();
    window.location.href = `/cart/${cartString}`;
  }

  resetSelection() {
    this.checkboxes.forEach(cb => {
      cb.checked = false;
      cb.closest('.single_product')?.classList.remove(this.classes.checked);
    });

    this.saveToLocalStorage();
    this.updateStickyCart();
  }

  setLoading(state) {
    this.addButton?.classList.toggle(this.classes.loading, state);
    this.addButton?.toggleAttribute('disabled', state);
  }

  showError(msg) {
    if (msg) alert(msg);
  }

  /* -----------------------------
   * Init
   * ----------------------------- */

  syncInitialState() {
    this.checkboxes.forEach(cb => {
      if (cb.checked) {
        cb.closest('.single_product')?.classList.add(this.classes.checked);
      }
    });

    this.saveToLocalStorage();
    this.updateStickyCart();
  }
}

customElements.define('bundle-products-mam', BundleProductsMam);


